'use strict'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { useHistory, useLocation } from 'react-router-dom'
import {
  makeStyles,
  Paper,
  Box,
  Snackbar,
  CircularProgress,
  Typography,
  Button
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import RefreshIcon from '@material-ui/icons/Refresh'
import WorkbenchHeader from './WorkbenchHeader'
import PropertiesTab from './PropertiesTab'
import CompareTab from './CompareTab'
import AnchorsTab from './AnchorsTab'
import MapTab from './MapTab'
import RoutesTab from './RoutesTab'
import CreateScenarioModal from './CreateScenarioModal'
import StalenessModal from './StalenessModal'
import PublishConfirmModal from './PublishConfirmModal'

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    padding: theme.spacing(2)
  },
  paper: {
    width: '100%'
  },
  tabContent: {
    padding: theme.spacing(2)
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(5)
  }
}))

function TerritoryWorkbench ({ url }) {
  const classes = useStyles()
  const history = useHistory()
  const location = useLocation()

  // Shared state
  const [scenarios, setScenarios] = useState([])
  const [selectedScenarioId, setSelectedScenarioId] = useState('')
  const [crews, setCrews] = useState([])
  const [routes, setRoutes] = useState([])
  const [loadingScenarios, setLoadingScenarios] = useState(true)
  const [error, setError] = useState(null)
  const [currentTab, setCurrentTab] = useState('properties')
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' })

  // Modal state
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [stalenessModalOpen, setStalenessModalOpen] = useState(false)
  const [publishModalOpen, setPublishModalOpen] = useState(false)
  const [publishingScenario, setPublishingScenario] = useState(false)
  const [publishDiffData, setPublishDiffData] = useState(null)
  const [staleFreshness, setStaleFreshness] = useState(null)
  const [pendingCreatePayload, setPendingCreatePayload] = useState(null)

  const requestedScenarioId = useMemo(
    () => (new URLSearchParams(location.search).get('scenarioId') || '').trim(),
    [location.search]
  )

  const selectedScenario = useMemo(
    () => scenarios.find(item => item._id === selectedScenarioId) || null,
    [scenarios, selectedScenarioId]
  )

  const isScenarioEditable = selectedScenario?.status === 'draft'

  const crewIds = useMemo(() => crews.map(c => c._id).sort(), [crews])
  const routeIds = useMemo(() => routes.map(r => r._id).sort(), [routes])

  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity })
  }, [])

  const downloadChangePackage = useCallback((changePackage) => {
    if (!changePackage?.csv) return false
    const blob = new Blob([changePackage.csv], { type: changePackage.contentType || 'text/csv;charset=utf-8;' })
    const objectUrl = window.URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = changePackage.fileName || `territory_publish_changes_${Date.now()}.csv`
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    window.URL.revokeObjectURL(objectUrl)
    return true
  }, [])

  const fetchScenarios = useCallback(async (preferredScenarioId = null) => {
    setLoadingScenarios(true)
    try {
      const response = await axios.get(`${url}/api/hmi/territory/scenarios`)
      const nextScenarios = response.data?.scenarios || []
      setScenarios(nextScenarios)

      if (preferredScenarioId && nextScenarios.some(item => item._id === preferredScenarioId)) {
        setSelectedScenarioId(preferredScenarioId)
      } else {
        setSelectedScenarioId(prev => {
          if (prev && nextScenarios.some(item => item._id === prev)) return prev
          const draft = nextScenarios.find(item => item.status === 'draft')
          return draft?._id || nextScenarios[0]?._id || ''
        })
      }
    } catch (err) {
      console.error('Error loading territory scenarios:', err)
      setError(err.message || 'Failed to load territory scenarios')
    } finally {
      setLoadingScenarios(false)
    }
  }, [url])

  const fetchCrews = useCallback(async (scenarioId) => {
    try {
      const response = await axios.get(`${url}/api/hmi/territory/properties`, {
        params: scenarioId ? { scenarioId } : undefined
      })
      const fetchedCrews = response.data?.crews || []
      const fetchedRoutes = response.data?.routes || []
      console.log('[TerritoryWorkbench] fetchCrews response — crews:', fetchedCrews.length, 'routes:', fetchedRoutes.length, 'sample route:', fetchedRoutes[0])
      setCrews(fetchedCrews)
      setRoutes(fetchedRoutes)
    } catch (err) {
      console.error('Error loading crews:', err)
    }
  }, [url])

  useEffect(() => {
    fetchScenarios(requestedScenarioId || null)
  }, [fetchScenarios, requestedScenarioId])

  useEffect(() => {
    fetchCrews(selectedScenarioId || null)
  }, [selectedScenarioId, fetchCrews])

  // Property counts from rows (passed up from PropertiesTab)
  const [propertyCounts, setPropertyCounts] = useState({ total: 0, vip: 0, locked: 0, unassigned: 0, anchors: 0 })

  // Create scenario flow
  const handleCreateSubmit = async (payload) => {
    setCreateModalOpen(false)
    const basePayload = {
      name: payload.name,
      sourceScenarioId: payload.sourceScenarioId,
      horizonDays: payload.horizonDays,
      seedMode: payload.seedMode
    }

    try {
      const response = await axios.post(`${url}/api/hmi/territory/scenarios`, basePayload)
      const scenario = response.data && response.data.scenario
      const createdId = scenario && scenario._id
      await fetchScenarios(createdId)
      if (scenario && scenario.isStaleAtCreation) {
        showSnackbar('Draft scenario created (routing data is stale — consider syncing)', 'warning')
      } else {
        showSnackbar('Draft scenario created', 'success')
      }
    } catch (err) {
      console.error('Error creating territory scenario:', err)
      var msg = 'Failed to create scenario'
      try { msg = err.response.data.message || msg } catch (e) {}
      showSnackbar(msg, 'error')
    }
  }

  const handleSyncNow = async () => {
    setStalenessModalOpen(false)
    try {
      await axios.post(`${url}/api/aspire/routing/sync`)
      showSnackbar('Aspire routing sync started. Wait a few minutes, then create the scenario again.', 'info')
    } catch (syncErr) {
      console.error('Error starting Aspire routing sync:', syncErr)
      showSnackbar(syncErr.response?.data?.message || syncErr.message || 'Failed to start sync', 'error')
    }
  }

  const handleProceedStale = async () => {
    setStalenessModalOpen(false)
    if (!pendingCreatePayload) return
    try {
      const response = await axios.post(`${url}/api/hmi/territory/scenarios`, {
        ...pendingCreatePayload,
        proceedWithStaleData: true
      })
      const createdId = response.data?.scenario?._id
      await fetchScenarios(createdId)
      showSnackbar('Draft scenario created (stale data acknowledged)', 'success')
    } catch (err) {
      console.error('Error creating scenario with stale acknowledgment:', err)
      showSnackbar(err.response?.data?.message || err.message || 'Failed to create scenario', 'error')
    }
  }

  // Publish flow
  const handlePublishClick = async () => {
    if (!selectedScenarioId || !isScenarioEditable) return
    try {
      const response = await axios.get(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/diff`, {
        params: { includeMetricsDelta: true }
      })
      setPublishDiffData(response.data || null)
      setPublishModalOpen(true)
    } catch (err) {
      console.error('Error fetching diff for publish:', err)
      showSnackbar(err.response?.data?.message || err.message || 'Failed to load publish preview', 'error')
    }
  }

  const handlePublishConfirm = async () => {
    if (!selectedScenarioId) return
    setPublishingScenario(true)
    try {
      const response = await axios.post(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}/publish`)
      const changePackage = response.data?.changePackage || null
      const downloaded = downloadChangePackage(changePackage)
      await fetchScenarios(selectedScenarioId)
      setPublishModalOpen(false)
      showSnackbar(
        downloaded
          ? `Scenario published and CSV downloaded (${changePackage.rowCount || 0} changes)`
          : 'Scenario published to live data',
        'success'
      )
    } catch (err) {
      console.error('Error publishing territory scenario:', err)
      showSnackbar(err.response?.data?.message || err.message || 'Failed to publish scenario', 'error')
    } finally {
      setPublishingScenario(false)
    }
  }

  const handleDeleteScenario = async () => {
    if (!selectedScenarioId) return
    const name = selectedScenario?.name || selectedScenarioId
    if (!window.confirm(`Delete scenario "${name}"? This cannot be undone.`)) return
    try {
      await axios.delete(`${url}/api/hmi/territory/scenarios/${selectedScenarioId}`)
      showSnackbar(`Scenario "${name}" deleted`, 'success')
      await fetchScenarios()
    } catch (err) {
      const data = err && err.response && err.response.data
      console.error('Error deleting scenario:', err)
      showSnackbar((data && data.message) || (err && err.message) || 'Failed to delete scenario', 'error')
    }
  }

  const refreshAll = useCallback(async () => {
    await fetchScenarios(selectedScenarioId || null)
  }, [fetchScenarios, selectedScenarioId])

  if (loadingScenarios) {
    return (
      <div className={classes.loading}>
        <CircularProgress />
      </div>
    )
  }

  if (error) {
    return (
      <Paper className={classes.paper}>
        <Box p={3}>
          <Typography color="error">Error: {error}</Typography>
          <Box mt={2}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refreshAll}>
              Retry
            </Button>
          </Box>
        </Box>
      </Paper>
    )
  }

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <WorkbenchHeader
          scenarios={scenarios}
          selectedScenarioId={selectedScenarioId}
          selectedScenario={selectedScenario}
          onScenarioChange={setSelectedScenarioId}
          onCreateNew={() => setCreateModalOpen(true)}
          onDelete={handleDeleteScenario}
          onPublish={handlePublishClick}
          onRefresh={refreshAll}
          onBack={() => history.push('/routing/territory/dashboard')}
          currentTab={currentTab}
          onTabChange={setCurrentTab}
          publishDisabled={!selectedScenarioId || !isScenarioEditable || publishingScenario}
          propertyCount={propertyCounts.total}
          vipCount={propertyCounts.vip}
          lockedCount={propertyCounts.locked}
          unassignedCount={propertyCounts.unassigned}
          anchorCount={propertyCounts.anchors}
        />

        <div className={classes.tabContent}>
          {currentTab === 'properties' && (
            <PropertiesTab
              url={url}
              selectedScenarioId={selectedScenarioId}
              isScenarioEditable={isScenarioEditable}
              crews={crews}
              crewIds={crewIds}
              routes={routes}
              routeIds={routeIds}
              onSnackbar={showSnackbar}
              onCountsChange={setPropertyCounts}
            />
          )}
          {currentTab === 'compare' && (
            <CompareTab
              url={url}
              selectedScenarioId={selectedScenarioId}
              scenarios={scenarios}
              crews={crews}
              crewIds={crewIds}
              routes={routes}
              routeIds={routeIds}
              onSnackbar={showSnackbar}
            />
          )}
          {currentTab === 'anchors' && (
            <AnchorsTab
              url={url}
              selectedScenarioId={selectedScenarioId}
              crews={crews}
              crewIds={crewIds}
              routes={routes}
              routeIds={routeIds}
              onSnackbar={showSnackbar}
            />
          )}
          {currentTab === 'map' && (
            <MapTab
              url={url}
              selectedScenarioId={selectedScenarioId}
              crews={crews}
              crewIds={crewIds}
              routes={routes}
              routeIds={routeIds}
              onSnackbar={showSnackbar}
            />
          )}
          {currentTab === 'routes' && (
            <RoutesTab
              url={url}
              onSnackbar={showSnackbar}
              onRoutesChanged={() => { if (selectedScenarioId) fetchCrews(selectedScenarioId) }}
            />
          )}
        </div>
      </Paper>

      {/* Modals */}
      <CreateScenarioModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        scenarios={scenarios}
        selectedScenarioId={selectedScenarioId}
        onSubmit={handleCreateSubmit}
      />
      <StalenessModal
        open={stalenessModalOpen}
        onClose={() => setStalenessModalOpen(false)}
        freshness={staleFreshness}
        onSyncNow={handleSyncNow}
        onProceedStale={handleProceedStale}
      />
      <PublishConfirmModal
        open={publishModalOpen}
        onClose={() => setPublishModalOpen(false)}
        onConfirm={handlePublishConfirm}
        scenarioName={selectedScenario?.name}
        diffData={publishDiffData}
        publishing={publishingScenario}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url
})

export default connect(mapStateToProps)(TerritoryWorkbench)
